import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Sparkles } from 'lucide-react'
import { useAudio } from '../../hooks/useAudio'
import { useSparkle } from '../../hooks/useSparkle'

// Math object emojis for counting
const MATH_OBJECTS = ['🍎', '🌟', '🦋', '🎈', '🍊', '🌸', '🔵', '🎨', '🐱', '🍇']

interface CountAndTapProps {
  targetNumber: number
  objectEmoji?: string
  onComplete: (correct: boolean) => void
}

export default function CountAndTap({ targetNumber, objectEmoji, onComplete }: CountAndTapProps) {
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [countdown, setCountdown] = useState(0)
  
  const { playCorrect, playWrong } = useAudio()
  const { miniBurst } = useSparkle()

  const emoji = objectEmoji || MATH_OBJECTS[targetNumber % MATH_OBJECTS.length]
  
  // Generate answer options (target ± 2, minimum 1)
  const options = [
    Math.max(1, targetNumber - 2),
    Math.max(1, targetNumber - 1),
    targetNumber,
    targetNumber + 1,
  ].filter((n, i, arr) => arr.indexOf(n) === i)

  const handleSelect = useCallback((number: number) => {
    if (showFeedback) return

    const correct = number === targetNumber
    setSelectedNumber(number)
    setIsCorrect(correct)
    setShowFeedback(true)
    
    // Play sound and effects
    if (correct) {
      playCorrect()
      miniBurst(0.5, 0.5)
    } else {
      playWrong()
    }
    
    // Differentiated timing: wrong answers get more time
    const feedbackDuration = correct ? 1200 : 2000
    setCountdown(Math.ceil(feedbackDuration / 1000))

    setTimeout(() => {
      onComplete(correct)
    }, feedbackDuration)
  }, [onComplete, showFeedback, targetNumber, playCorrect, playWrong, miniBurst])
  
  // Countdown timer for visual feedback
  useEffect(() => {
    if (!showFeedback || countdown <= 0) return
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    
    return () => clearInterval(timer)
  }, [showFeedback, countdown])

  // Smaller grid for mobile - max 3 rows
  const gridCols = targetNumber <= 6 ? 'grid-cols-3' : targetNumber <= 9 ? 'grid-cols-3' : 'grid-cols-4'

  return (
    <div className="w-full max-w-sm mx-auto px-3">
      {/* Compact Question */}
      <motion.div 
        className="text-center mb-3"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <span className="text-sm text-gray-500 block mb-1">Count the {emoji}s!</span>
      </motion.div>

      {/* Counting Grid - Compact */}
      <motion.div 
        className={`grid ${gridCols} gap-2 mb-4 justify-items-center`}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.2 }}
      >
        {Array.from({ length: targetNumber }, (_, i) => (
          <motion.div
            key={i}
            className="w-10 h-10 sm:w-11 sm:h-11 bg-[var(--math-surface-elevated)] rounded-xl flex items-center justify-center text-2xl shadow-sm"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: i * 0.03, duration: 0.15 }}
          >
            {emoji}
          </motion.div>
        ))}
      </motion.div>

      {/* Number Options - 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3">
        {options.map((number, index) => {
          const isSelected = selectedNumber === number
          const showAsCorrect = showFeedback && number === targetNumber
          const showAsWrong = showFeedback && isSelected && number !== targetNumber

          return (
            <motion.button
              key={number}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.2 }}
              whileHover={!showFeedback ? { scale: 1.03 } : {}}
              whileTap={!showFeedback ? { scale: 0.97 } : {}}
              onClick={() => handleSelect(number)}
              disabled={showFeedback}
              className={`
                h-16 rounded-2xl font-bold text-2xl
                flex items-center justify-center
                transition-all duration-150
                ${showAsCorrect 
                  ? 'bg-[var(--math-success)] text-white shadow-lg' 
                  : showAsWrong 
                    ? 'bg-[var(--math-error)] text-white'
                    : isSelected 
                      ? 'bg-[var(--math-primary)] text-white shadow-md'
                      : 'bg-white shadow-sm hover:shadow-md border-2 border-gray-100'
                }
              `}
            >
              {number}
            </motion.button>
          )
        })}
      </div>

      {/* Enhanced Feedback with Countdown */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-center mt-3"
          >
            {isCorrect ? (
              <div className="flex items-center justify-center gap-2 bg-[var(--math-success)]/10 px-4 py-2 rounded-full">
                <Sparkles className="w-5 h-5 text-[var(--math-success)]" />
                <span className="font-bold text-[var(--math-success)]">Great counting! {targetNumber}!</span>
                <span className="text-xl">🎉</span>
                {countdown > 0 && (
                  <span className="text-xs text-[var(--math-success)]/60 ml-1">{countdown}</span>
                )}
              </div>
            ) : (
              <div className="bg-[var(--math-error)]/10 px-3 py-2 rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-500">You counted:</span>
                  <span className="font-bold text-[var(--math-error)]">{selectedNumber}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-gray-500">Correct count:</span>
                  <span className="font-bold text-[var(--math-success)]">{targetNumber}</span>
                </div>
                <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden max-w-[120px] mx-auto">
                  <motion.div
                    className="h-full bg-[var(--math-error)]"
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: 2, ease: 'linear' }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
